import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { DISPLAY_LABELS } from '../utils/constants';
import { useAuth } from '../firebase/AuthContext';

/**
 * ResultDisplay Component
 * 
 * A fully responsive, mobile-optimized results screen for the Acquaintance Game.
 * Features:
 * - iOS-style design optimized for touch interfaces
 * - Responsive layout that works well on all mobile devices
 * - Subtle animations and transitions for improved user experience
 * - High-contrast text and accessible design elements
 * - Optimized tap targets for better mobile interaction
 * - Minimalist aesthetic focused on content clarity
 * - Haptic feedback for enhanced interaction feedback
 *
 * This component handles:
 * 1. Displaying game results (success/failure)
 * 2. Showing correct/incorrect answers
 * 3. Providing progress tracking
 * 4. Managing victory state when all works are completed
 * 5. Offering navigation options to continue gameplay
 *
 * @param {Object} props - Component props
 * @returns {JSX.Element} The rendered results interface optimized for mobile
 */
const ResultDisplay = ({
  gameResult,
  questions,
  currentWork,
  debugInfo,
  askedQuestions,
  restartGame,
  goToSubjectSelection,
  completedWorks,
  allWorksCompleted,
  failureReason
}) => {
  // Get auth context to check if user is logged in
  const { currentUser } = useAuth();
  
  // Animation state for game result feedback
  const [animationComplete, setAnimationComplete] = useState(false);
  const [expandedAnswers, setExpandedAnswers] = useState(false);
  
  // Track if component is mounted to prevent state updates after unmount
  const isMounted = useRef(true);
  
  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  /**
   * Initialize UI animations and progress counts
   */
  useEffect(() => {
    // Set animation with slight delay to ensure proper mounting
    const animationTimer = setTimeout(() => {
      if (isMounted.current) {
        setAnimationComplete(true);
      }
    }, 100);
    
    return () => {
      clearTimeout(animationTimer);
    };
  }, [completedWorks]);
  
  /**
   * Determines if the user provided an answer for a question
   * Used to style answer displays differently based on answer state
   * 
   * @param {Object} info - Debug info for the question containing user input
   * @returns {boolean} Whether an answer was provided
   */
  const hasAnswer = (info) => {
    return info && info.userInput && info.userInput.trim() !== '';
  };

  /**
   * Formats user answers for display with appropriate styling
   * Handles empty answers gracefully with placeholder text
   * 
   * @param {Object} info - Debug info for the question containing user input
   * @returns {string} Formatted answer text for display
   */
  const formatUserAnswer = (info) => {
    if (!info || !hasAnswer(info)) {
      return '(No answer provided)';
    }
    return info.userInput;
  };
  
  /**
   * Calculates and formats user performance metrics
   * Creates a summary of user performance with percentage calculations
   * 
   * @returns {Object} Performance metrics including counts, percentages and feedback text
   */
  const getPerformanceData = () => {
    // Count questions asked and correctly answered
    const asked = Array.from(askedQuestions).length;
    const correct = Object.values(debugInfo)
      .filter(info => info.wasAsked && info.isCorrect)
      .length;
    const incorrect = asked - correct;
    
    // Calculate percentage correct (avoid division by zero)
    const percentage = asked > 0 ? Math.round((correct / asked) * 100) : 0;
    
    // New points system: 
    // - 0 points if all answers wrong
    // - 1 point for each correct answer (max 4)
    const points = correct === 0 ? 0 : correct;
    
    // Return formatted data with appropriate feedback message
    return {
      correct,
      asked,
      incorrect,
      percentage,
      points,
      summary: `${correct} out of ${asked} correct`,
      // Only show "Well done!" when ALL answers are correct (100%)
      label: percentage === 100 ? 'Well done!' : 'Keep studying!'
    };
  };
  
  // Prepare data for display
  // Make sure we show all questions in results, not just the ones in askedQuestions
  const askedQuestionsList = questions;
  const performanceData = getPerformanceData();

  // Victory state - show special completion screen
  if (allWorksCompleted) {
    return (
      <div className="result-container ios-card animate-in" style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '14px', // Slightly smaller radius
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)', // Lighter shadow
        padding: '20px', // Reduced padding
        margin: '16px',
        maxWidth: '500px',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '70px' // Add margin to avoid overlap with bottom nav
      }}>
        {/* Victory header with celebration icons */}
        <div className={`victory-header ${animationComplete ? 'animate-bounce' : ''}`} style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px' // Reduced margin
        }}>
          <div className="confetti-icon" style={{ fontSize: '28px', marginRight: '10px' }}>🎉</div>
          <h2 className="victory-heading" style={{
            fontSize: '24px', // Slightly smaller font
            fontWeight: '700',
            color: '#1C1C1E',
            margin: 0
          }}>Congratulations!</h2>
          <div className="confetti-icon" style={{ fontSize: '28px', marginLeft: '10px' }}>🎉</div>
        </div>
        
        {/* Victory message card - more minimal */}
        <div className="victory-message card-appear card-delay-1" style={{
          backgroundColor: 'rgba(52, 199, 89, 0.08)', // Lighter background
          borderRadius: '10px', // Smaller radius
          padding: '16px', // Reduced padding
          marginBottom: '20px', // Reduced margin
          textAlign: 'center'
        }}>
          <p style={{ 
            color: '#007F44', 
            fontSize: '15px', // Smaller font
            fontWeight: '500',
            margin: 0,
            lineHeight: '1.4'
          }}>Great job! You've earned points for your knowledge!</p>
          
          {/* Achievement badge - more minimal */}
          <div className="celebration-badge" style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#34C759',
            borderRadius: '18px', // Smaller radius
            padding: '8px 16px', // Reduced padding
            marginTop: '16px', // Reduced margin
            boxShadow: '0 2px 4px rgba(52, 199, 89, 0.2)' // Lighter shadow
          }}>
            <span className="badge-icon" style={{ fontSize: '18px', marginRight: '8px' }}>🏆</span>
            <span className="badge-text" style={{ color: 'white', fontWeight: '700', fontSize: '15px' }}>Literary Expert</span>
          </div>
        </div>
        
        {/* Login advertisement for non-logged in users */}
        {!currentUser && (
          <div className="login-ad card-appear card-delay-2" style={{
            marginBottom: '20px',
            padding: '12px',
            backgroundColor: 'rgba(0, 122, 255, 0.05)',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '1px dashed rgba(0, 122, 255, 0.2)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <span style={{ fontSize: '18px', marginRight: '8px' }}>🏆</span>
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#007AFF'
              }}>
                Log in to save your achievement!
              </span>
            </div>
            <p style={{
              margin: '0 0 10px 0',
              fontSize: '13px',
              textAlign: 'center',
              color: '#3A3A3C',
              lineHeight: '1.4'
            }}>
              Create an account to track your literary achievements, record your score, and compete with college friends on the leaderboard.
            </p>
            <Link 
              to="/login" 
              style={{
                textDecoration: 'none',
                color: 'white',
                backgroundColor: '#007AFF',
                fontSize: '14px',
                fontWeight: '600',
                padding: '8px 16px',
                borderRadius: '8px',
                display: 'inline-block',
                boxShadow: '0 2px 4px rgba(0, 122, 255, 0.2)'
              }}
            >
              Log In or Sign Up
            </Link>
          </div>
        )}
        
        {/* Action button - more minimal */}
        <div className="result-buttons card-appear card-delay-3" style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px' // Reduced margin
        }}>
          <button 
            onClick={goToSubjectSelection}
            aria-label="Return to subject selection"
            className="btn-change-subject ios-button touch-feedback"
            style={{
              backgroundColor: '#007AFF',
              color: 'white',
              border: 'none',
              borderRadius: '10px', // Smaller radius
              padding: '14px 20px', // Reduced padding
              fontSize: '16px', // Smaller font
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 6px rgba(0, 122, 255, 0.3)', // Lighter shadow
              minHeight: '48px', // Slightly reduced height
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation'
            }}
          >
            <span className="button-icon" style={{ marginRight: '10px', fontSize: '18px' }}>←</span>
            <span className="button-text">Try Another Course</span>
          </button>
        </div>
      </div>
    );
  }

  // Regular result display (success or failure)
  return (
    <div className="result-container ios-card animate-in" style={{
      backgroundColor: '#FFFFFF',
      borderRadius: '14px',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
      padding: '20px',
      margin: '16px',
      maxWidth: '500px',
      marginLeft: 'auto',
      marginRight: 'auto',
      marginBottom: '70px'
    }}>
      {/* Simplified result header with clean aesthetics */}
      <div 
        className={`result-header ${gameResult ? 'success-header' : 'failure-header'} ${animationComplete ? 'animate-bounce' : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px'
        }}
      >
        <div className="result-badge" style={{
          width: '40px',
          height: '40px',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '12px',
          fontSize: '18px',
          fontWeight: '600',
          backgroundColor: gameResult ? '#34C759' : '#FF3B30',
          color: 'white'
        }}>
          {gameResult ? '✓' : '✗'}
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column'
        }}>
          <h2 className="result-title" style={{
            fontSize: '22px',
            fontWeight: '600',
            color: gameResult ? '#007F44' : '#CF0029',
            margin: '0 0 4px 0'
          }}>
            {gameResult ? 'Correct!' : 'Incorrect'}
          </h2>
          <div style={{
            fontSize: '15px',
            color: '#8A8A8E'
          }}>
            {`${performanceData.correct} out of ${performanceData.asked} correct • ${performanceData.points} Points`}
          </div>
        </div>
      </div>
      
      {/* Work title - displayed for context */}
      <div style={{
        textAlign: 'center',
        marginBottom: '24px',
        padding: '10px',
        backgroundColor: '#F2F2F7',
        borderRadius: '10px'
      }}>
        <div style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#3A3A3C'
        }}>
          "{currentWork.work}"
        </div>
      </div>
      
      {/* Incorrect answers are now shown in a more minimalistic format */}
      {!gameResult && failureReason && (
        <div className="answer-correction card-appear card-delay-1" style={{
          backgroundColor: 'rgba(255, 59, 48, 0.05)',
          borderRadius: '12px',
          padding: '12px',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '15px',
            color: '#3A3A3C',
            margin: 0,
            lineHeight: '1.4',
            fontWeight: '500'
          }}>{failureReason}</p>
        </div>
      )}
      
      {/* Streamlined Answer Summary - more minimal version */}
      <div className="answers-comparison card-appear card-delay-2" style={{
        marginBottom: '24px'
      }}>
        {/* Interactive header with toggle button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <h3 style={{
            fontSize: '15px',
            fontWeight: '600',
            color: '#1C1C1E',
            margin: 0
          }}>Answers</h3>
          <button 
            onClick={() => setExpandedAnswers(!expandedAnswers)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '13px',
              color: '#007AFF',
              padding: '8px 12px',
              borderRadius: '8px',
              fontWeight: '500'
            }}
            aria-label={expandedAnswers ? "Hide answer details" : "Show answer details"}
          >
            {expandedAnswers ? 'Hide' : 'Show'}
          </button>
        </div>
        
        {/* Minimalistic answer list */}
        {expandedAnswers && (
          <div className="answers-list" style={{
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}>
            {askedQuestionsList.map((q, index) => {
              const info = debugInfo[q.type] || {};
              const hasUserAnswer = hasAnswer(info);
              const isCorrect = info.isCorrect;
              
              return (
                <div 
                  key={q.type} 
                  style={{
                    padding: '10px 12px',
                    backgroundColor: isCorrect ? 'rgba(52, 199, 89, 0.03)' : 'rgba(255, 59, 48, 0.03)',
                    borderBottom: index < askedQuestionsList.length - 1 ? '1px solid rgba(229,229,234,0.4)' : 'none'
                  }}
                >
                  {/* Question type with status icon */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '4px'
                  }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#1C1C1E'
                    }}>
                      {DISPLAY_LABELS[q.type] || q.type}
                    </div>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '8px',
                      backgroundColor: isCorrect ? '#34C759' : '#FF3B30',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>
                      {isCorrect ? '✓' : '✗'}
                    </div>
                  </div>
                  
                  {/* Clean answer comparison */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    columnGap: '6px',
                    rowGap: '2px',
                    fontSize: '12px'
                  }}>
                    <div style={{ color: '#8A8A8E' }}>You:</div>
                    <div style={{
                      color: hasUserAnswer ? '#1C1C1E' : '#8A8A8E',
                      fontStyle: hasUserAnswer ? 'normal' : 'italic',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap'
                    }}>
                      {formatUserAnswer(info)}
                    </div>
                    
                    <div style={{ color: '#8A8A8E' }}>Correct:</div>
                    <div style={{
                      color: '#1C1C1E',
                      fontWeight: '500',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap'
                    }}>
                      {currentWork[q.type]}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Login advertisement for non-logged in users */}
      {!currentUser && (
        <div className="login-ad card-appear card-delay-3" style={{
          marginBottom: '24px',
          padding: '12px',
          backgroundColor: 'rgba(0, 122, 255, 0.05)',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          border: '1px dashed rgba(0, 122, 255, 0.2)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <span style={{ fontSize: '18px', marginRight: '8px' }}>🏆</span>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#007AFF'
            }}>
              Signup to save your progress!
            </span>
          </div>
          <p style={{
            margin: '0 0 10px 0',
            fontSize: '13px',
            textAlign: 'center',
            color: '#3A3A3C',
            lineHeight: '1.4'
          }}>
            Create an account to track points, save your progress, and compete with college friends on the leaderboard.
          </p>
          <Link 
            to="/login" 
            style={{
              textDecoration: 'none',
              color: 'white',
              backgroundColor: '#007AFF',
              fontSize: '14px',
              fontWeight: '600',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'inline-block',
              boxShadow: '0 2px 4px rgba(0, 122, 255, 0.2)'
            }}
          >
            Register!
          </Link>
        </div>
      )}
      
      {/* Minimalist action buttons */}
      <div className="result-buttons card-appear card-delay-3" style={{
        display: 'flex',
        gap: '12px',
        flexDirection: 'column'
      }}>
        {/* Primary action button */}
        <button 
          onClick={restartGame}
          aria-label={gameResult ? "Next Work" : "Try again"}
          style={{
            backgroundColor: gameResult ? '#34C759' : '#007AFF',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            padding: '14px',
            fontSize: '16px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: gameResult 
              ? '0 2px 6px rgba(52, 199, 89, 0.3)' 
              : '0 2px 6px rgba(0, 122, 255, 0.3)',
            minHeight: '48px',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation'
          }}
        >
          <span style={{ marginRight: '10px', fontSize: '18px' }}>
            {gameResult ? '→' : '↻'}
          </span>
          <span>{gameResult ? 'Next Work' : 'Try Again'}</span>
        </button>
        
        {/* Secondary action button */}
        <button 
          onClick={goToSubjectSelection}
          aria-label="Change subject"
          style={{
            backgroundColor: 'transparent',
            color: '#007AFF',
            border: '1px solid #007AFF',
            borderRadius: '10px',
            padding: '14px',
            fontSize: '16px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '48px',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation'
          }}
        >
          <span style={{ marginRight: '10px', fontSize: '18px' }}>←</span>
          <span>Change Subject</span>
        </button>
      </div>
      
      {/* Debug info - Only in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="debug-info" style={{
          marginTop: '24px',
          borderTop: '1px solid #E5E5EA',
          paddingTop: '16px'
        }}>
          <details>
            <summary style={{
              fontSize: '13px',
              color: '#8A8A8E',
              cursor: 'pointer',
              userSelect: 'none'
            }}>Debug Info (Dev Only)</summary>
            <pre style={{
              fontSize: '11px',
              overflow: 'auto',
              padding: '8px',
              backgroundColor: '#F2F2F7',
              borderRadius: '6px',
              marginTop: '8px',
              color: '#3A3A3C',
              maxHeight: '200px'
            }}>
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

ResultDisplay.propTypes = {
  gameResult: PropTypes.bool.isRequired,
  questions: PropTypes.array.isRequired,
  currentWork: PropTypes.object.isRequired,
  debugInfo: PropTypes.object.isRequired,
  askedQuestions: PropTypes.instanceOf(Set).isRequired,
  restartGame: PropTypes.func.isRequired,
  goToSubjectSelection: PropTypes.func.isRequired,
  completedWorks: PropTypes.instanceOf(Set).isRequired,
  allWorksCompleted: PropTypes.bool.isRequired,
  failureReason: PropTypes.string
};

export default ResultDisplay; 